import axios, {AxiosResponse} from 'axios';
import {
    CodesRetrieveResponse,
    ColumnRetrieveResponse, DuplicateSubjectsResponse,
    ExcelExportResponse,
    MappingRuleCDMSVisitCodeRetrieveResponse,
    MappingRuleSummaryRetrieveResponse,
    MappingRuleSyncTargetAuditTrailResponse,
    NullDataResponse,
    ResetSyncRequest,
    SelectedAeSyncsRequest,
    StudiesRetrieveResponse,
    SyncAuditTrailRequest,
    SyncAuditTrailResponse,
    SyncCubeCDMSItemRetrieveResponse,
    SyncOption,
    SyncProgressDoneCheckRequest
} from '@/model/CommonModel';
import ApiUtils from '@/utils/ApiUtils';

export default class SyncApi {
    public static retrieveStudies(sponsorKey: string, params: any = {}): Promise<StudiesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveStudyMappingRules(sponsorKey: number, studyKey: number, params: any = {}): Promise<StudiesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveStudySyncList(sponsorKey: number, studyKey: number, params: any = {}): Promise<StudiesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/edcsync/${studyKey}`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveCDMSTreeItem(sponsorKey: number | string, stdKey: number | string): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${stdKey}/mapping/item-tree`)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveMappingRuleSummaryList(sponsorKey: number | string, studyKey: number | string, itemId: string)
    : Promise<MappingRuleSummaryRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/${itemId}`)
            .then((res: AxiosResponse) => res.data);
    }

    public static getSafetyItemAuditTrails(sponsorKey: string, targetKey: number): Promise<MappingRuleSyncTargetAuditTrailResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${targetKey}/mapping/audits`)
            .then((res: AxiosResponse) => res.data);
    }

    public static getCdmsItemAuditTrails(sponsorKey: string,
                                         targetKey: number, seqNo: number): Promise<MappingRuleSyncTargetAuditTrailResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${targetKey}/mapping/${seqNo}/target/audits`)
            .then((res: AxiosResponse) => res.data);
    }

    public static getCodeConversionItemAuditTrails(sponsorKey: string, targetKey: number,
                                                   seqNo: number, params: any = {}): Promise<MappingRuleSyncTargetAuditTrailResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${targetKey}/mapping/${seqNo}/code-conversion/audits`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static downloadMappingRules(sponsorKey: string, studyKey: string): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/download`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }

    public static initializeMappingRules(sponsorKey: string, studyKey: string): Promise<any> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/initialize`)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveCubeCDMSItems(sponsorKey: number | string, studyKey: number| string,
                                        subjKey: number | string, params: any = {}): Promise<SyncCubeCDMSItemRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/edcsync/${studyKey}/subject/${subjKey}`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static syncAE(sponsorKey: number | string, studyKey: number | string, subjKey: number | string,
                         params: any = {}): Promise<any> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/edcsync/${studyKey}/subject/${subjKey}`, params)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveMappingRuleCDMSVisit(sponsorKey: number | string,
                                               studyKey: number | string,
                                               itemKey: number | string,
                                               verKey: number | string): Promise<MappingRuleCDMSVisitCodeRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/itemId/visit?itemKey=${itemKey}&verKey=${verKey}`)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveSourceCode(sponsorKey: number | string,
                                     studyKey: number | string, itemKey: number | string): Promise<CodesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/sourceCode/${itemKey}`)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveTargetCode(sponsorKey: number | string,
                                     studyKey: number | string, codeId: string): Promise<CodesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/targetCode/${codeId}`)
            .then((res: AxiosResponse) => res.data);
    }

    public static saveMappingRule(sponsorKey: number | string, studyKey: number | string, itemId: string, param: any) {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/${itemId}/save`, param)
            .then((res: AxiosResponse) => res.data);
    }

    public static selectedAeSyncs(sponsorKey: number | string, studyKey: number | string,
                                  request: SelectedAeSyncsRequest[]): Promise<any> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/edcsync/${studyKey}/list-sync`, request)
            .then((res: AxiosResponse) => res.data);
    }

    public static getDeselectedSubject(sponsorKey: number | string, studyKey: number | string,
                                       request: SelectedAeSyncsRequest[]): Promise<DuplicateSubjectsResponse> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/edcsync/${studyKey}/list-sync/SUBJECT/check`, request)
            .then((res: AxiosResponse) => res.data);
    }


    public static release(sponsorKey: number | string, studyKey: number | string, params: any = {}): Promise<any> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/mapping/release`, null, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveSponsorStudyNumber(sponsorKey: number | string, stdKey: string) {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${stdKey}/STD_SPN_NO`)
            .then((res: AxiosResponse) => res.data);
    }

    public static ResetSync(params: ResetSyncRequest): Promise<NullDataResponse> {
        const {sponsorKey, stdKey, resetAeInfos, reason} = params;
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/sync/${stdKey}/reset`, {resetAeInfos, reason})
            .then((res: AxiosResponse) => res.data);
    }

    public static getSyncAuditTrail(param: SyncAuditTrailRequest): Promise<SyncAuditTrailResponse> {
        const {sponsorKey, stdKey, syncInfo: params} = param;
        return axios.get(`/1.0/sponsors/${sponsorKey}/sync/${stdKey}/audit-trail`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static isSyncProgressDoneCheck(sponsorKey: string, stdKey: string,
                                          requests: SyncProgressDoneCheckRequest[]) {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/edcsync/${stdKey}/progress-check`, requests)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveSyncListColumn(sponsorKey: string, studyKey: string, params: any = {}): Promise<ColumnRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/sync/${studyKey}/columns`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static exportSyncListExcel(stdKey: string): Promise<ExcelExportResponse> {
        return axios
          .get(`/1.0/stdKey/${stdKey}/edcsync/export`)
          .then((res: AxiosResponse) => res.data);
    }
}
